"use client";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import useAnalytics from "../../../../analytics/useAnalytics";
import { CURRENCY_ID } from "../../../../constants/common/common";
import { setGaDateSelection } from "../../../../analytics/gaEvents";
import { getOutOfStock } from "../../../../utils/pdp/nonDeliverable";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import {
  requestToFetchGetDeliveryDateAndGeoPriceAction,
  resetShippingLoader,
  setDeliveryDetailsAction,
} from "../../../../store/slices/common/shippingDateAndSlotSelection";
import { onSmoothScrollToElement, parseDate } from "../../../../utils/common";
import DeliveryDateSelection from "./deliveryDateSelection";
import { GA4_EVENTS } from "../../../../analytics/gaConstant";
import { getMultiOrderDateRange } from "../../../../utils/pdp/common";
import { calenderIsTodayOrTomorrowDate } from "../../../../utils/common/deliveryDate";
import useCleverTapCartData from "../../../../customHooks/clevertap/cart";
import DeliveryShippingAndTimeSlotSelection from "./deliveryShippingAndTimeSlotSelection";
import {
  setDeliveryDateErrorFromAddToCart,
  setDeliveryTimeSlotErrorFromAddToCart,
  setSelectedDateFromAddToCart,
} from "../../../../store/slices/pdpSlice";
import Toast from "../../../atoms/toast";
import { constants, productTypes } from "../../../../constants/pageConstants/pdpConstants";

/**
 * Represents the DomesticShippingDateAndSlotSelection component for selecting the date, shipping method, and time slot.
 *
 * This component allows the user to select a delivery date, choose a shipping method, and select a time slot for
 * domestic shipping. It handles navigation between the calendar and shipping method selection components.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.currentItem - The item being shipped, containing information such as catalog ID, stock
 *   availability, etc.
 * @param {string} [props.classes=""] - Additional classes to apply to the component's wrapper. Defaults to an empty string.
 * @param {boolean} [props.isCart=false] - Flag indicating whether the component is being used in the cart context.
 *   Defaults to `false`.
 * @param {Object} props.deliveryDates - The available delivery dates for the item, including details such as blocked dates.
 * @param {Object} props.blockedDates - Details about blocked dates for delivery, including any catalog content
 *   restrictions or special messages.
 * @param {boolean} props.isCutoffTimestamp - Flag indicating whether there is a cutoff timestamp for delivery selection.
 * @param {Object} props.msgContent - Contains message content for special catalog messages (e.g., same-day delivery)
 *   or other relevant delivery information.
 * @param {string} props.pincode - The pincode for the shipping location to determine available shipping methods.
 * @param {boolean} props.isMobile - Flag indicating whether the component is being viewed on a mobile device.
 * @param {string} props.selectedCurrency - The selected currency for the transaction.
 *
 * @returns {React.ReactElement} A React element rendering the shipping date and slot selection UI, including
 *   the calendar and shipping methods components.
 */
const DomesticShippingDateAndSlotSelection = ({
  onToggleDeliveryDetailsPopUp,
  currentItem,
  classes = "",
  isCart = false,
  deliveryDates,
  isCutoffTimestamp,
  msgContent,
  pincode,
  isMobile,
  selectedCurrency,
  popupCleverTapData,
  setShowDeliveryDetailsDrawer,
  cleverTapData,
  cartCleverTapData,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const updatedCleverTapData = useRef(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [hasTimeSlot, setHasTimeSlot] = useState(false);
  const [hasDateSelector, setHasDateSelector] = useState(false);
  const [multiOrderDateRange, setMultiOrderDateRange] = useState([]);
  const [isSameDateSelected, setIsSameDateSelected] = useState(false);
  const [nonDeliverableToast, setNonDeliverableToast] = useState(false);
  const { isMultiOrderProduct, multiOrderProductDetails } = currentItem;
  const [currentShippingMethod, setCurrentShippingMethod] = useState({});
  const [userSelectedTimeSlotId, setUserSelectedTimeSlotId] = useState(null);
  const [isSameShippingSelected, setIsSameShippingSelected] = useState(false);
  const [multiOrderProductChange, setMultiOrderProductChange] = useState(false);

  const {
    clientVariantType,
    deliveryDateError,
    deliveryTimeSlotError,
    selectedDeliveryDetails,
    nextAvailableDifference,
    shippingDetails,
    isCartVisible,
  } = useSelector(
    (state) => ({
      clientVariantType: state?.pdp?.clientVariantType,
      deliveryDateError: state?.pdp?.deliveryDateError,
      deliveryTimeSlotError: state?.pdp?.deliveryTimeSlotError,
      selectedDeliveryDetails: state?.shippingDateAndSlotSelection.selectedDeliveryDetails,
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      isCartVisible: state?.cart?.isCartVisible,
      shippingDetails: state?.shippingDateAndSlotSelection?.shippingDetails,
    }),
    shallowEqual,
  );
  const isOutOfStock = getOutOfStock(currentItem?.productStockAvailable, currentItem?.productId);
  const isDateToday = selectedDate ? calenderIsTodayOrTomorrowDate(selectedDate)?.isDateToday : false;
  const isDateTomorrow = selectedDate ? calenderIsTodayOrTomorrowDate(selectedDate)?.isDateTomorrow : false;

  const isCourier = isCart
    ? currentItem?.primaryProductCategoryId === "COURIER" || currentItem?.shippingMethodName === "Courier"
    : currentItem?.primaryCategoryId === "COURIER" || shippingDetails?.selectedShippingMethod === productTypes.COURIER;

  const cleverTapHooksData = useCleverTapCartData({
    currentItem,
    clientVariantType,
    nextAvailableDifference,
    isSameDateSelected,
    isSameShippingSelected,
    userSelectedTimeSlotId,
    isDateToday,
    isDateTomorrow,
  });

  const clevertapDeliveryData = {
    ...cleverTapHooksData,
    ...cartCleverTapData,
  };

  /**
   * Handles the selection of a date in the calendar.
   * After selecting a date, it sets the selected date in state and moves the current component to the delivery method.
   *
   * @function
   * @param {string} date - The selected date from the calendar.
   * @returns {void}
   */
  const onDateSelection = (date) => {
    setSelectedDate(date);
    if (!isCartVisible) {
      onSmoothScrollToElement("date-chip-section", isMobile ? 70 : 150);
    }
    dispatch(resetShippingLoader());
    if (!isCourier) {
      setCurrentShippingMethod({ shippingMethodId: "", shippingMethodName: "" });
    }
    if (isMultiOrderProduct) {
      const lastDeliverableMultiOrderDate =
        deliveryDates?.deliveryDates[
          Number(deliveryDates?.deliveryDates?.length) - Number(multiOrderProductDetails?.noOfDays)
        ];
      if (parseDate(lastDeliverableMultiOrderDate) < parseDate(date)) {
        setNonDeliverableToast(parseDate(lastDeliverableMultiOrderDate) < parseDate(date));
        setMultiOrderDateRange([]);
        setMultiOrderProductChange(false);
      } else {
        setNonDeliverableToast(false);
        setMultiOrderProductChange(true);
        setMultiOrderDateRange(getMultiOrderDateRange(date, multiOrderProductDetails));
      }
    }
    dispatch(setDeliveryDateErrorFromAddToCart(false));
    dispatch(setSelectedDateFromAddToCart(date));

    trackEvent(
      GA4_EVENTS?.DELIVERY_DATE_SELECTION,
      {
        ...setGaDateSelection(date, "pdp_interaction", GA4_EVENTS?.DELIVERY_DATE_SELECTION),
      },
      ["ga4"],
    );

    if (isCart) {
      setIsSameDateSelected(selectedDate === currentItem?.deliveryDate);
      updatedCleverTapData.current = {
        ...clevertapDeliveryData,
        date_of_delivery: date,
        delivery_shipping: "",
        delivery_shipping_fee: "",
        delivery_slot: "",
      };
    } else {
      updatedCleverTapData.current = {
        ...popupCleverTapData,
        is_date_today: calenderIsTodayOrTomorrowDate(date)?.isDateToday,
        is_date_tomorrow: calenderIsTodayOrTomorrowDate(date)?.isDateTomorrow,
        date_of_delivery: date,
        delivery_shipping: "",
        delivery_shipping_fee: "",
        delivery_slot: "",
      };
    }
    trackEvent(cleverTapCommonEvents.deliveryDateSelected, updatedCleverTapData.current, ["clevertap"]);
  };

  useEffect(() => {
    if (isCartVisible) {
      setSelectedDate(currentItem?.shippingDetails?.deliveryDate?.fullDeliveryDate || "");
      setHasTimeSlot(true);
    } else {
      setSelectedDate("");
    }
  }, [selectedDeliveryDetails?.selectedDate]);

  /**
   * Handles the selection of time and shipping method.
   * Combines the selected time and shipping method with the selected date, and calls the onHandleDeliveryDetailsClick callback.
   *
   * @function
   * @param {Object} timeNShippingMethod - An object containing the selected time slot and shipping method.
   * @returns {void}
   */
  const onTimeAndShippingMethodSelection = (timeNShippingMethod) => {
    dispatch(setDeliveryDetailsAction({ ...timeNShippingMethod, selectedDate }));
    const queryParams = {
      productId: currentItem?.productId,
      pinCode: pincode,
      currencyUomId: CURRENCY_ID,
      deliveryDate: selectedDate,
      catalogId: currentItem?.catalogId,
    };
    dispatch(requestToFetchGetDeliveryDateAndGeoPriceAction(queryParams));
    if (isCart) {
      onToggleDeliveryDetailsPopUp(timeNShippingMethod, selectedDate);
    }
  };

  /**
   * Toggles the delivery error toast visibility based on the error state.
   */
  const onToggleDeliveryErrorToast = () => {
    if (deliveryDateError) {
      dispatch(setDeliveryDateErrorFromAddToCart(false));
    } else if (deliveryTimeSlotError) {
      dispatch(setDeliveryTimeSlotErrorFromAddToCart(false));
    }
  };
  /**
   * Renders the toast notification based on the error state.
   */
  const renderToast = () => {
    let header = "";
    if (deliveryDateError) {
      header = constants.SELECT_DATE_PDP;
    } else if (deliveryTimeSlotError) {
      header = constants.SELECT_TIME_PDP;
    }
    return (
      <div className="fixed bottom-[5rem] left-1/2 z-50 w-[255px] -translate-x-1/2 rounded-lg bg-white-900">
        <Toast variant="info" isToast={true} header={header} onToastChange={onToggleDeliveryErrorToast} />
      </div>
    );
  };

  useEffect(() => {
    if (isCart) {
      setHasDateSelector(true);
    }
  }, [isCart]);

  return (
    <div className={`w-[100%] ${classes} flex flex-col gap-16`} id="date-chip-section">
      <DeliveryDateSelection
        currentItem={currentItem}
        selectedCurrency={selectedCurrency}
        isMobile={isMobile}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        setHasTimeSlot={setHasTimeSlot}
        hasDateSelector={hasDateSelector}
        setHasDateSelector={setHasDateSelector}
        msgContent={msgContent}
        onDateSelection={onDateSelection}
        isCutoffTimestamp={isCutoffTimestamp}
        nonDeliverableToast={nonDeliverableToast}
        setNonDeliverebleToast={setNonDeliverableToast}
        isOutOfStock={isOutOfStock}
        cleverTapData={cleverTapData}
        hasMultiOrderProductChange={multiOrderProductChange}
        multiOrderDateRange={multiOrderDateRange}
        popupCleverTapData={popupCleverTapData}
        currentShippingMethod={currentShippingMethod}
        isCart={isCart}
        setShowDeliveryDetailsDrawer={setShowDeliveryDetailsDrawer}
        isCourier={isCourier}
      />
      {selectedDate && (
        <DeliveryShippingAndTimeSlotSelection
          currentItem={currentItem}
          isMobile={isMobile}
          selectedDate={selectedDate}
          selectedCurrency={selectedCurrency}
          onTimeAndShippingMethodSelection={onTimeAndShippingMethodSelection}
          setUserSelectedTimeSlotId={setUserSelectedTimeSlotId}
          cutoffTimestamp={isCutoffTimestamp}
          isCart={isCart}
          currentShippingMethod={currentShippingMethod}
          setCurrentShippingMethod={setCurrentShippingMethod}
          setHasTimeSlot={setHasTimeSlot}
          hasTimeSlots={hasTimeSlot}
          setHasDateSelector={setHasDateSelector}
          setIsSameShippingSelected={setIsSameShippingSelected}
          popupCleverTapData={popupCleverTapData}
          isCourier={isCourier}
        />
      )}
      <>{(deliveryDateError || deliveryTimeSlotError) && renderToast()}</>
    </div>
  );
};
DomesticShippingDateAndSlotSelection.propTypes = {
  currentItem: PropTypes.object.isRequired,
  classes: PropTypes.string,
  isCart: PropTypes.bool,
  deliveryDates: PropTypes.object,
  isCutoffTimestamp: PropTypes.bool,
  isMobile: PropTypes.bool,
  msgContent: PropTypes.object,
  pincode: PropTypes.string,
  selectedCurrency: PropTypes.object,
  popupCleverTapData: PropTypes.object,
  cleverTapData: PropTypes.object,
  cartCleverTapData: PropTypes.object,
  onToggleDeliveryDetailsPopUp: PropTypes.func,
  setShowDeliveryDetailsDrawer: PropTypes.func,
};
export default DomesticShippingDateAndSlotSelection;
